import Image from 'next/image'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import type {JSX} from 'react'
import {
	Icon as CIcon,
	type IconSize,
} from '@/products/flexible-web-toolkit/components/icon'
import {loadImageFromKontentAI} from '@/image-loader'
import type {Block} from '@/services/kontent-ai/types'
import {RichTextRenderer} from '@/products/flexible-web-toolkit/components/rich-text-renderer'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {ReusableCTAButtonComponent} from '@/products/flexible-web-toolkit/blocks/cta-button'
import type {CtaButtonContentItem} from '@/products/flexible-web-toolkit/blocks/cta-button'

export type IconWithTextAndCtaContentItem = IContentItem<{
	icon: Elements.AssetsElement
	/** @deprecated Use brandColor instead */
	brand: Elements.MultipleChoiceElement
	brandColor: Elements.CustomElement
	size: Elements.MultipleChoiceElement
	title: Elements.TextElement
	content: Elements.RichTextElement
	boldTitle: Elements.MultipleChoiceElement
	buttons: Elements.LinkedItemsElement<CtaButtonContentItem>
}>

export const IconWithTextAndCtaBlock: Block<IconWithTextAndCtaContentItem> = ({
	block,
	page,
	...context
}): JSX.Element => {
	const {hexCode: iconBorderHex} = colorWithFallback({
		blockColor: block.elements.brandColor,
		blockColorLegacy: block.elements.brand,
		pageConfig: page.elements.config[0],
		fallbackColorId: 'primary',
	})

	return (
		<div className="flex flex-col items-center py-4 text-center">
			<CIcon
				borderHexColor={iconBorderHex ?? undefined}
				size={(block.elements.size[0]?.codename || 'lg') as IconSize}
			>
				<div className="relative h-full w-full">
					{block.elements.icon[0] ? (
						<Image
							alt={block.elements.icon[0].description ?? ''}
							fill
							loader={loadImageFromKontentAI}
							src={block.elements.icon[0].url}
						/>
					) : null}
				</div>
			</CIcon>
			{block.elements.title ? (
				<strong
					className={
						block.elements.boldTitle[0]?.codename === 'bold'
							? 'mt-4 font-bold'
							: 'mt-4 font-normal'
					}
				>
					{block.elements.title}
				</strong>
			) : null}
			<RichTextRenderer
				element={block.elements.content}
				page={page}
				{...context}
			/>
			{block.elements.buttons.length > 0 ? (
				<div className="mt-4 flex gap-2">
					{block.elements.buttons.map((button) => (
						<ReusableCTAButtonComponent
							block={button}
							key={button.system.id}
							page={page}
							{...context}
						/>
					))}
				</div>
			) : null}
		</div>
	)
}
